<template>
  <div class="profile-page">
    <div class="card">
      <h2 class="card-title">{{ $t('profile.title') }}</h2>
      <p class="text-muted mb-0 small">
        {{ $t('profile.body') }}
      </p>
      <!-- 系統訊息 -->
      <div v-if="state.success" class="alert alert-success">{{ state.success }}</div>
      <div v-if="state.error" class="alert alert-danger">{{ state.error }}</div>

      <!-- 電子郵件（Auth 為真相） -->
      <div class="form-row">
        <label class="form-label">
          {{ $t('profile.email.label') }} <span v-if="!userEmail" class="req"></span>
        </label>

        <!-- 已有 email：唯讀 -->
        <template v-if="userEmail">
          <div class="readonly">{{ userEmail }}</div>
        </template>

        <!-- 無 email：可輸入 + 寄送驗證信 -->
        <template v-else>
          <div class="email-input-row">
            <input
              v-model.trim="form.email"
              type="email"
              class="form-control"
              :placeholder="$t('member.inputEmail')"
              @input="emailState.error=''; emailState.sent=''"
            />
            <button
              class="btn btn-mini"
              :disabled="!emailValid || emailState.sending || emailState.cooldown > 0"
              @click="onSendEmailVerification"
              :title="emailValid
                ? (emailState.cooldown>0
                    ? $t('profile.email.cooldownTitle', { s: emailState.cooldown })
                    : $t('profile.email.sendTitle'))
                : $t('profile.email.invalidTitle')"
            >
              <span v-if="emailState.sending" class="spinner"></span>
              <template v-if="emailState.cooldown > 0">
                {{ $t('profile.email.resendIn', { s: emailState.cooldown }) }}
              </template>
              <template v-else>
                {{ $t('profile.email.send') }}
              </template>
            </button>
          </div>

          <small class="hint" v-if="emailState.sent">
            {{ emailState.sent }}
            <span v-html="$t('profile.email.checkSpamHint')"></span>
          </small>
          <small class="hint" v-if="emailState.error" style="color:#a0131a">{{ emailState.error }}</small>
        </template>
      </div>

      <!-- PHONE_VERIFICATION_DISABLED_START: 手機輸入＋OTP 驗證 -->
      
      <div class="form-row">
        <label class="form-label">{{ $t('profile.phone.title') }}</label>

        <template v-if="phoneLocked">
          <div class="input-col">
            <div class="readonly">{{ prettyPhone(originalPhone) || originalPhone }}</div>
            <small class="hint">{{ $t('profile.phone.alreadyverified') }}</small>
          </div>
        </template>

        <template v-else>
          <div class="input-col">
            <div class="email-input-row">
              <input
                v-model.trim="form.phone"
                type="tel"
                class="form-control"
                :placeholder="userEmail ? '09xx-xxx-xxx' : '請先完成 Email 綁定'"
                :disabled="!userEmail || phoneState.sending"
                @input="phoneState.error=''"
              />
              <button
                class="btn btn-mini"
                :disabled="!userEmail || !phoneValid || phoneState.sending || (phoneState.asked && phoneState.cooldown>0)"
                @click="phoneState.asked ? onResendPhoneOtp() : onSendPhoneOtp()"
                :title="!userEmail ? '請先完成 Email 驗證' : (phoneValid ? '寄送簡訊驗證碼' : '請輸入有效的手機號碼')"
              >
                <span v-if="phoneState.sending" class="spinner"></span>
                <template v-if="phoneState.asked && phoneState.cooldown>0">
                  重送（{{ phoneState.cooldown }}s）
                </template>
                <template v-else-if="phoneState.asked">
                  重新發送
                </template>
                <template v-else>
                  發送驗證碼
                </template>
              </button>
            </div>

            <small class="hint" v-if="phoneState.sent">{{ phoneState.sent }}</small>
            <small
              v-if="phoneState.error"
              class="hint full-row error-text"
            >
              {{ phoneState.error }}
            </small>


            <div v-if="phoneState.asked" style="display:grid; grid-template-columns: 1fr auto; gap:8px; align-items:center;">
              <input
                v-model.trim="phoneState.code"
                class="form-control"
                inputmode="numeric"
                maxlength="6"
                placeholder="輸入 6 碼驗證碼"
                @input="phoneState.error=''"
              />
              <button
                class="btn btn-mini"
                :disabled="phoneState.verifying || phoneState.code.length !== 6"
                @click="onVerifyPhoneOtp"
              >
                <span v-if="phoneState.verifying" class="spinner"></span>
                驗證
              </button>
            </div>
          </div>
        </template>
      </div>
      
      <!-- PHONE_VERIFICATION_DISABLED_END -->

      <!-- 身分類型（必填） -->
      <div class="form-row">
        <label class="form-label">{{ $t('profile.identityType.label') }} <span class="req">*</span></label>
        <div class="select-row">
          <select v-model="form.identity_type" class="form-control">
            <option value="" disabled>{{ $t('profile.identityType.placeholder') }}</option>
            <option value="doctor">{{ $t('profile.identityType.doctor') }}</option>
            <option value="nurse">{{ $t('profile.identityType.nurse') }}</option>
            <option value="consultant">{{ $t('profile.identityType.consultant') }}</option>
            <option value="procurement">{{ $t('profile.identityType.procurement') }}</option>
            <option value="owner">{{ $t('profile.identityType.owner') }}</option>
          </select>
        </div>

      </div>

      <!-- 姓名（必填） -->
      <div class="form-row">
        <label class="form-label">{{ $t('profile.name.label') }} <span class="req">*</span></label>
        <input v-model.trim="form.name" type="text" class="form-control" :placeholder="$t('profile.name.placeholder')" />
      </div>

      <!-- 醫院/診所/機構名稱（必填） -->
      <div class="form-row">
        <label class="form-label">{{ $t('profile.recipient.label') }} <span class="req">*</span></label>
        <input v-model.trim="form.recipient_name" type="text" class="form-control" :placeholder="$t('profile.recipient.placeholder')" />
      </div>

      <!-- 地址（縣市 / 區 / 郵遞區號 / 路段） -->
      <div class="form-row">
        <label class="form-label">{{ $t('profile.address.label') }}</label>
        <div class="input-col">
          <div class="select-row">
            <select v-model="addr.city" class="form-control" @change="onCityChange">
              <option value="" disabled>{{ $t('profile.address.cityPlaceholder') }}</option>
              <option v-for="c in cities" :key="c" :value="c">{{ c }}</option>
            </select>

            <select v-model="addr.district" class="form-control" :disabled="!addr.city" @change="onDistrictChange">
              <option value="" disabled>{{ $t('profile.address.districtPlaceholder') }}</option>
              <option v-for="d in districtsOfSelectedCity" :key="d.name" :value="d.name">
                {{ d.name }}
              </option>
            </select>

            <input
              class="form-control zip-input"
              :value="addr.zip"
              :placeholder="$t('profile.address.zipLabel')"
              readonly
              style="max-width:110px"
              :title="$t('profile.address.zipTitle')"
            />
          </div>

          <input
            v-model.trim="addr.detail"
            type="text"
            class="form-control"
            :placeholder="$t('profile.address.detailPlaceholder')"
          />

          <small class="hint" v-if="composedAddress">
            {{ composedAddress }}
          </small>
        </div>
      </div>

      <!-- 邀請碼（唯讀顯示） -->
      <!-- <div class="form-row">
        <label class="form-label">{{ $t('profile.inviteCode.label') }}</label>
        <div class="readonly">
          <template v-if="inviteCode">
            {{ inviteCode }}
          </template>
          <template v-else>
            {{ $t('profile.inviteCode.empty') }}
          </template>
        </div>
      </div> -->

      <!-- 輸入他人邀請碼 -->
      <!-- <div class="form-row">
        <label class="form-label">{{ $t('profile.invitationCode.label') }}</label>
        <div class="input-col">
          
          <template v-if="inviteLocked">
            <div class="readonly">
              <span v-if="inviteInput">{{ inviteInput }}</span>
              <span v-else>{{ $t('profile.invitationCode.bound') }}</span>
            </div>
            <small class="hint">
              {{ $t('profile.invitationCode.lockHint') }}
            </small>
          </template>

          
          <template v-else>
            <div class="email-input-row invite-row">
              <input
                v-model.trim="inviteInput"
                type="text"
                class="form-control"
                :placeholder="$t('profile.invitationCode.placeholder')"
                @input="state.error=''; state.success=''"
              />
              <button
                class="btn btn-mini"
                :disabled="state.saving || !inviteInput"
                @click="onBindInviteCode"
              >
                {{ $t('profile.invitationCode.save') }}
              </button>

              
              <small class="hint invite-hint">
                {{ $t('profile.invitationCode.hint') }}
              </small>
            </div>
          </template>
        </div>
      </div> -->

      <!-- 驗證狀態 -->
        <div class="form-row">
          <label class="form-label">{{ $t('profile.verifyStatus') }}</label>
          <div class="verify-status-row">
            <span
              class="verify-pill"
              :class="{
                ok: identityStatus === 'verified',
                pending: identityStatus === 'pending',
                danger: identityStatus === 'unverified'
              }"
            >
              {{ $t(`profile.status.${identityStatus}`) }}
            </span>

            <!-- 小問號按鈕 -->
            <button
              type="button"
              class="icon-help-btn"
              @click="openVerifyHelp = true"
              :title="$t('verifyStatusHelp.title')"
            >
              ?
            </button>
          </div>
        </div>




      <div class="actions">
        <button class="btn btn-primary" :disabled="state.saving" @click="onSave">
          <span v-if="state.saving" class="spinner"></span>
          {{ $t('common.save') }}
        </button>


        <button class="btn btn-ghost" @click="openPassword = true">{{ $t('profile.actions.changePassword') }}</button>
      </div>
    </div>
  </div>

  <!-- 修改密碼 Modal -->
  <div class="pw-modal" v-if="openPassword" @click.self="openPassword = false">
    <div class="modal-card" role="dialog" aria-modal="true">
      <h3 class="modal-title">{{ $t('profile.password.title') }}</h3>

      <div v-if="pwdState.success" class="alert alert-success">{{ pwdState.success }}</div>
      <div v-if="pwdState.error" class="alert alert-danger">{{ pwdState.error }}</div>

      <div class="form-row">
        <label class="form-label">{{ $t('member.newPassword') }} <span class="req">*</span></label>
        <input v-model="pwdForm.password" type="password" class="form-control" minlength="8" :placeholder="$t('passwordUpdate.minLength')" />
      </div>
      <div class="form-row">
        <label class="form-label">{{ $t('member.configNewPassword') }} <span class="req">*</span></label>
        <input v-model="pwdForm.confirm" type="password" class="form-control" minlength="8" />
      </div>

      <div class="actions">
        <button class="btn btn-primary" :disabled="pwdState.loading" @click="onChangePassword">
          <span v-if="pwdState.loading" class="spinner"></span>
          {{ $t('member.configChange') }}
        </button>
        <button class="btn btn-ghost" @click="openPassword = false">{{ $t('common.cancel') }}</button>
      </div>
    </div>
  </div>

    <!-- 驗證狀態說明 Modal -->
  <div class="pw-modal" v-if="openVerifyHelp" @click.self="openVerifyHelp = false">
    <div class="modal-card verify-help-card" role="dialog" aria-modal="true">
      <div class="verify-help-header">
        <h3 class="modal-title">{{ $t('profile.verifyStatusHelp.title') }}</h3>
        <button
          type="button"
          class="icon-close-btn"
          @click="openVerifyHelp = false"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div class="verify-help-grid">

        <div class="verify-help-item">
          <div class="verify-pill danger">{{ $t('profile.status.unverified') }}</div>
          <p class="verify-help-caption">
            {{ $t('profile.verifyStatusHelp.unverified') }}
          </p>
        </div>

        <div class="verify-help-item">
          <div class="verify-pill pending">{{ $t('profile.status.pending') }}</div>
          <p class="verify-help-caption">
            {{ $t('profile.verifyStatusHelp.pending') }}
          </p>
        </div>

        <div class="verify-help-item">
          <div class="verify-pill ok">{{ $t('profile.status.verified') }}</div>
          <p class="verify-help-caption">
            {{ $t('profile.verifyStatusHelp.verified') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { supabase } from '@/lib/supabase'
import { useRouter } from 'vue-router'
import districtsData from '@/lib/taiwan_districts.json'
import { getCurrentInstance } from 'vue'

const { proxy } = getCurrentInstance()
const t = (key, params) => proxy.$t(key, params)
const router = useRouter()

/** -------- 基本狀態 -------- */
const userId = ref(null)
const userEmail = ref('') // 僅用 Auth 的 email
const userPhone = ref('') // 僅用 Auth 的 phone（手機登入者通常會有）
const emailRequired = computed(() => !userPhone.value) // ✅ 只有「不是手機登入」才要求 Email
const state = ref({ saving: false, error: '', success: '' })
const inviteCode = ref('')      // 自己的邀請碼（Txxxxx）
const inviteInput = ref('')     // 使用者輸入的別人邀請碼
const inviteLocked = ref(false) // 是否已經綁定過邀請人

//const isVerified   = computed(() => identityStatus.value === 'verified')
//const isPending    = computed(() => identityStatus.value === 'pending')
//const isUnverified = computed(() => identityStatus.value === 'unverified')


const form = ref({
  identity_type: '',
  name: '',
  recipient_name: '',
  /* PHONE_VERIFICATION_DISABLED_FIELD: phone 暫時保留欄位但不顯示、不上傳 */
  phone: '',
  address: '',
  email: '' // 當 auth 沒 email 時，暫存使用者輸入，用來寄驗證信
})

/** -------- Email 驗證（UI 狀態 + 冷卻）-------- */
const emailState = ref({ sending: false, sent: '', error: '', cooldown: 0 })
const emailValid = computed(() => /\S+@\S+\.\S+/.test((form.value.email || '').trim()))
let emailCooldownTimer = null

/** -------- 手機鎖定（是否已綁定） -------- */
/* PHONE_VERIFICATION_DISABLED_STATE: 僅保留原始值，避免其他程式引用爆炸 */
const originalPhone = ref('')                // 保留 E.164（+886…）
const phoneLocked = computed(() => !!originalPhone.value)

/** -------- 手機驗證（UI 狀態 + 冷卻） -------- */
/* PHONE_VERIFICATION_DISABLED_STATE*/
const phoneState = ref({
  sending: false,
  verifying: false,
  asked: false,
  code: '',
  cooldown: 0,
  error: '',
  sent: ''
})
let phoneCooldownTimer = null


/** -------- 認證狀態展示 -------- */
const identityStatus = ref('unverified')

/** -------- 修改密碼 -------- */
const openPassword = ref(false)
const pwdForm = ref({ password: '', confirm: '' })
const pwdState = ref({ loading: false, error: '', success: '' })

// 驗證說明 Modal 開關
const openVerifyHelp = ref(false)

/** 認證必填檢查（不含手機） */
const verificationReady = computed(() => {
  const baseOk = Boolean(
    (form.value.identity_type || '').trim() &&
    (form.value.name || '').trim() &&
    (form.value.recipient_name || '').trim()
  )

  // ✅ 手機登入（Auth 有 phone）→ 不要求 Email
  if (!emailRequired.value) return baseOk

  // ✅ 其他情況 → 要求 Email（Auth email 或輸入中的 email）
  const hasEmail = Boolean((userEmail.value || '').trim() || (form.value.email || '').trim())
  return baseOk && hasEmail
})


/** -------- 地址下拉 -------- */
const cities = districtsData.map(c => c.name)
const addr = ref({ city: '', district: '', zip: '', detail: '' })

const districtsOfSelectedCity = computed(() => {
  if (!addr.value.city) return []
  const found = districtsData.find(c => c.name === addr.value.city)
  return found ? found.districts : []
})
function onCityChange() {
  addr.value.district = ''
  addr.value.zip = ''
}
function onDistrictChange() {
  const found = districtsOfSelectedCity.value.find(d => d.name === addr.value.district)
  addr.value.zip = found ? found.zip : ''
}
const composedAddress = computed(() => {
  const parts = [
    (addr.value.zip || '').trim(),
    (addr.value.city || '').trim(),
    (addr.value.district || '').trim(),
    (addr.value.detail || '').trim()
  ].filter(Boolean)
  return parts.join(' ')
})
function normalizeWhitespace(str = '') {
  return String(str).replace(/\s+/g, ' ').trim()
}
function parseAddressToDropdowns(addressStr = '') {
  addr.value = { city: '', district: '', zip: '', detail: '' }
  const raw = normalizeWhitespace(addressStr)
  if (!raw) return
  let zip = ''
  let rest = raw
  const zipMatch = raw.match(/^(\d{3})(\d{2})?\s*/)
  if (zipMatch) { zip = zipMatch[1]; rest = raw.slice(zipMatch[0].length).trim() }
  let cityObj = districtsData.find(c => rest.startsWith(c.name))
  if (!cityObj) {
    const candidate = districtsData.find(c => rest.includes(c.name))
    if (candidate) { const idx = rest.indexOf(candidate.name); rest = rest.slice(idx + candidate.name.length).trim(); cityObj = candidate }
  } else {
    rest = rest.slice(cityObj.name.length).trim()
  }
  if (!cityObj) { addr.value.zip = zip; addr.value.detail = rest; return }
  addr.value.city = cityObj.name
  if (rest.startsWith(cityObj.name)) rest = rest.slice(cityObj.name.length).trim()
  let distObj = cityObj.districts.find(d => rest.startsWith(d.name))
  if (!distObj) {
    distObj = cityObj.districts.find(d => rest.replace(/^\s+/, '').startsWith(d.name))
    if (distObj) rest = rest.replace(/^\s+/, '')
  }
  if (distObj) { addr.value.district = distObj.name; addr.value.zip = zip || distObj.zip; rest = rest.slice(distObj.name.length).trim() }
  else { addr.value.zip = zip }
  if (rest.startsWith(cityObj.name)) rest = rest.slice(cityObj.name.length).trim()
  if (addr.value.district && rest.startsWith(addr.value.district)) rest = rest.slice(addr.value.district.length).trim()
  addr.value.detail = rest
}

/** -------- 手機格式工具：台灣 09 <-> +886 E.164 -------- */
/* PHONE_VERIFICATION_DISABLED_UTILS */
function normalizeTaiwanMobile(raw='') {
  const only = String(raw).replace(/\D/g, '')
  if (/^09\d{8}$/.test(only)) return { e164: '+886' + only.slice(1), local: only }
  if (/^8869\d{8}$/.test(only)) return { e164: '+' + only, local: '0' + only.slice(3) }
  if (/^\+8869\d{8}$/.test(raw)) return { e164: raw, local: '0' + raw.replace(/^\+886/, '') }
  return null
}
function e164ToLocalTaiwan(e164 = '') {
  const m = String(e164).match(/^\+?886(9\d{8})$/)
  return m ? '0' + m[1] : ''
}
function prettyPhone(e164 = '') {
  const m = String(e164).match(/^\+?886(9\d{3})(\d{3})(\d{3})$/)
  return m ? `+886 ${m[1]} ${m[2]} ${m[3]}` : e164
}
const phoneValid = computed(() => !!normalizeTaiwanMobile(form.value.phone || ''))

function ensurePlusE164(p = '') {
  if (!p) return p
  const s = String(p).trim()
  if (s.startsWith('+')) return s
  if (/^8869\d{8}$/.test(s)) return `+${s}`
  if (/^\d+$/.test(s)) return `+${s}`
  return s
}

/** -------- Lifecycle -------- */
onMounted(async () => {
  const { data: userRes, error: userErr } = await supabase.auth.getUser()
  if (userErr || !userRes?.user) {
    router.replace('/member')
    return
  }
  userId.value = userRes.user.id
  userEmail.value = userRes.user.email || ''
  userPhone.value = userRes.user.phone || '' // ✅ 取 Auth phone

  await ensureProfile()
  const profileRow = await loadProfile()

  // ★ 初次鏡像：把 Auth 的 email/phone 帶進 profiles（只在 profiles 為空時）
  await mirrorAuthToProfilesOnce(userRes.user.email || null, userRes.user.phone || null, profileRow)
})
onUnmounted(() => {
  if (emailCooldownTimer) clearInterval(emailCooldownTimer)
  /* PHONE_VERIFICATION_DISABLED_TIMER
  if (phoneCooldownTimer) clearInterval(phoneCooldownTimer)
  */
})

/** -------- Profile CRUD -------- */
async function ensureProfile() {
  await supabase.from('user_profiles').upsert({ id: userId.value }, { onConflict: 'id' })
}
async function loadProfile() {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('identity_type, name, recipient_name, phone, email, address, identity_status, invite_code, invited_by, invited_code')
    .eq('id', userId.value)
    .maybeSingle()

  if (error) { state.value.error = error.message || '讀取資料失敗'; return null }

  form.value.identity_type  = data?.identity_type   || ''
  form.value.phone = e164ToLocalTaiwan(data?.phone || '') || ''
  form.value.name           = data?.name            || ''
  form.value.recipient_name = data?.recipient_name  || ''
  // PHONE_VERIFICATION_DISABLED_NOTE: 不顯示手機，但仍讀入以便未來開啟
  // form.value.phone        = e164ToLocalTaiwan(data?.phone || '') || (data?.phone || '')
  form.value.address        = data?.address         || ''
  parseAddressToDropdowns(form.value.address || '')
  originalPhone.value       = data?.phone           || ''     // E.164
  identityStatus.value      = data?.identity_status || 'unverified'
  inviteCode.value          = data?.invite_code     || ''
  inviteLocked.value        = !!data?.invited_by
  inviteInput.value         = data?.invited_code    || ''

  return data || null
}

/** 初次鏡像：僅在 profiles.email/phone 為空時，寫入 Auth 的值 */
async function mirrorAuthToProfilesOnce(authEmail, authPhoneE164, currentProfileRow) {
  try {
    const row = currentProfileRow ?? (await supabase
      .from('user_profiles')
      .select('email, phone')
      .eq('id', userId.value)
      .maybeSingle()).data

    if (!row) return

    const patch = { id: userId.value }
    let need = false

    if (!row.email && authEmail) { patch.email = authEmail; need = true }
    if (!row.phone && authPhoneE164) {
      patch.phone = ensurePlusE164(authPhoneE164)
      need = true
    }

    if (need) {
      const { error } = await supabase
        .from('user_profiles')
        .upsert(patch, { onConflict: 'id' })
      if (error) throw error
    }
  } catch (e) {
    console.warn('[mirrorAuthToProfilesOnce] failed:', e?.message || e)
  }
}

// 只在「profiles.phone 為空」且「Auth 有 phone」時，補一次；其他情況完全不動 phone
async function buildPhonePatchIfNeeded() {
  // 已經有 phone（DB 讀進來的 originalPhone），就不要改它
  if (originalPhone.value) return {}

  // 去 auth 拿最新 phone（手機註冊者通常在這裡）
  const { data, error } = await supabase.auth.getUser()
  if (error) return {}

  const authPhone = data?.user?.phone || ''
  if (!authPhone) return {}

  // 回寫到 profiles（只補一次）
  const e164 = ensurePlusE164(authPhone)
  return { phone: e164 }
}

async function onSave() {
  state.value.error = ''
  state.value.success = ''
  state.value.saving = true
  try {
    // 沒填完就不讓送，因為「送出＝請求驗證」
    if (!verificationReady.value) {
      state.value.error = emailRequired.value
        ? '請完整填寫：身分類型、姓名、機構名稱，以及電子郵件'
        : '請完整填寫：身分類型、姓名、機構名稱'
      return
    }


    // 決定這次要寫進去的身份狀態：
    // - 如果已經 verified 就保持
    // - 其他狀態（unverified / pending）只要能送出，就視為 pending
    let nextStatus = identityStatus.value
    if (identityStatus.value !== 'verified') {
      nextStatus = 'pending'
    }

   

    const phonePatch = await buildPhonePatchIfNeeded()

    const payload = {
      id: userId.value,
      identity_type: (form.value.identity_type || '').trim() || null,
      name: (form.value.name || '').trim() || null,
      recipient_name: (form.value.recipient_name || '').trim() || null,
      ...phonePatch, // ✅ 只有需要時才寫 phone
      address: composedAddress.value || null,
      identity_status: nextStatus
    }


    const { error } = await supabase.from('user_profiles').upsert(payload, { onConflict: 'id' })
    if (error) throw error

    identityStatus.value = nextStatus
    state.value.success = nextStatus === 'pending' ? '已儲存並送出認證' : '已儲存'
  } catch (e) {
    state.value.error = e?.message || '儲存失敗'
  } finally {
    state.value.saving = false
  }
}

/** 重新讀取 auth user，回傳是否已有 email */
async function refreshAuthEmail() {
  const { data, error } = await supabase.auth.getUser()
  if (error) return false
  const latest = data?.user?.email || ''
  if (latest) {
    userEmail.value = latest
    await mirrorAuthToProfilesOnce(latest, null, null)
    return true
  }
  return false
}

function resetPhoneInput(keepError = true) {
  const prevError = phoneState.value.error

  form.value.phone = ''
  phoneState.value = {
    sending: false,
    verifying: false,
    asked: false,
    code: '',
    cooldown: 0,
    error: keepError ? prevError : '',
    sent: ''
  }

  if (phoneCooldownTimer) {
    clearInterval(phoneCooldownTimer)
    phoneCooldownTimer = null
  }
}

/** -------- Email 驗證：寄送 + 冷卻 -------- */
async function onSendEmailVerification() {
  emailState.value.error = ''
  emailState.value.sent = ''

  if (!emailValid.value) {
    emailState.value.error = '請輸入有效的 Email'
    return
  }

  const target = form.value.email.trim().toLowerCase()

  emailState.value.sending = true
  try {
    // ✅ 先檢查是否已被其他帳號使用
    const { data: available, error: chkErr } = await supabase
      .rpc('check_email_available', { p_email: target })

    if (chkErr) throw chkErr
    if (!available) {
      throw new Error('此 Email 已被其他帳號使用，請更換')
    }

    // ✅ 綁定（不是登入）
    const { error } = await supabase.auth.updateUser({ email: target })
    if (error) throw error

    emailState.value.sent = '已寄出驗證信，請到信箱點擊連結完成綁定。'
    startEmailCooldown(60)
  } catch (e) {
    emailState.value.error = e?.message || '寄送驗證信失敗'
  } finally {
    emailState.value.sending = false
  }
}

function startEmailCooldown(seconds) {
  if (emailCooldownTimer) clearInterval(emailCooldownTimer)
  emailState.value.cooldown = seconds
  emailCooldownTimer = setInterval(async () => {
    if (emailState.value.cooldown <= 1) {
      clearInterval(emailCooldownTimer)
      emailCooldownTimer = null
      emailState.value.cooldown = 0
      const verified = await refreshAuthEmail()
      if (verified) window.location.reload()
      return
    }
    emailState.value.cooldown -= 1
  }, 1000)
}

/** -------- 手機：OTP 相關方法（綁定版，不登入） -------- */
async function onSendPhoneOtp() {
  phoneState.value.error = ''
  phoneState.value.sent = ''

  if (phoneLocked.value) {
    phoneState.value.error = '此手機已完成綁定，若需更改請聯繫客服。'
    return
  }
  if (!userEmail.value) {
    phoneState.value.error = '請先完成 Email 綁定'
    return
  }

  const parsed = normalizeTaiwanMobile(form.value.phone || '')
  if (!parsed) {
    phoneState.value.error = '請輸入有效的手機號碼'
    return
  }

  phoneState.value.sending = true
  try {
    // ✅ 先檢查手機是否已被使用
    const { data: available, error: chkErr } = await supabase
      .rpc('check_phone_available', { p_phone: parsed.e164 })

    if (chkErr) throw chkErr
    if (!available) {
      throw new Error('此手機已被其他帳號使用')
    }

    // ✅ 綁定流程（寄 OTP，不登入）
    const { error } = await supabase.auth.updateUser({ phone: parsed.e164 })
    if (error) throw error

    phoneState.value.asked = true
    phoneState.value.code = ''
    phoneState.value.sent = '驗證碼已寄送，請輸入 6 碼完成綁定'
    startPhoneCooldown(60)
  } catch (e) {
    const msg = String(e?.message || '')
    const code = e?.code

    if (
      msg.includes('phone number has already been registered') ||
      code === '23505'
    ) {
      phoneState.value.error = t('profile.phone.alreadyRegistered')
      resetPhoneInput()
    } else {
      phoneState.value.error = t('common.operationFailed')
    }
  }
}

async function onResendPhoneOtp() {
  phoneState.value.error = ''
  phoneState.value.sent = ''

  if (phoneLocked.value) {
    phoneState.value.error = '此手機已完成綁定，若需更改請聯繫客服。'
    return
  }
  if (!userEmail.value) {
    phoneState.value.error = '請先完成 Email 綁定'
    return
  }

  const parsed = normalizeTaiwanMobile(form.value.phone || '')
  if (!parsed) {
    phoneState.value.error = '請輸入有效的手機號碼'
    return
  }

  if (phoneState.value.cooldown > 0) {
    phoneState.value.error = `請稍後再試（${phoneState.value.cooldown}s）`
    return
  }

  phoneState.value.sending = true
  try {
    // ✅ 仍然是「綁定」重送：再呼叫一次 updateUser({ phone })
    const { error } = await supabase.auth.updateUser({ phone: parsed.e164 })
    if (error) throw error

    phoneState.value.asked = true
    phoneState.value.sent = '已重新寄送驗證碼'
    startPhoneCooldown(60)
  } catch (e) {
    phoneState.value.error = e?.message || '重送驗證碼失敗'
  } finally {
    phoneState.value.sending = false
  }
}

async function onVerifyPhoneOtp() {
  phoneState.value.error = ''
  phoneState.value.sent = ''

  if (phoneLocked.value) {
    phoneState.value.error = '此手機已完成綁定，若需更改請聯繫客服。'
    return
  }

  const parsed = normalizeTaiwanMobile(form.value.phone || '')
  if (!parsed) {
    phoneState.value.error = '手機格式錯誤'
    return
  }

  const token = (phoneState.value.code || '').trim()
  if (!token || token.length !== 6) {
    phoneState.value.error = '請輸入 6 碼驗證碼'
    return
  }

  phoneState.value.verifying = true
  try {
    // ✅ phone 綁定驗證：type 用 phone_change（不是 sms 登入）
    const { error } = await supabase.auth.verifyOtp({
      phone: parsed.e164,
      token,
      type: 'phone_change'
    })
    if (error) throw error

    // ✅ 重新抓 auth user，確保是「同一個登入者」已成功綁上 phone
    const { data: u2, error: u2Err } = await supabase.auth.getUser()
    if (u2Err) throw u2Err

    const authedPhone = u2?.user?.phone || parsed.e164

    // ✅ 寫入 profiles.phone（你已加唯一，若被佔用會噴錯）
    const { error: upErr } = await supabase
      .from('user_profiles')
      .update({ phone: authedPhone })
      .eq('id', userId.value)

    if (upErr) {
      // 常見：unique violation
      if (upErr.code === '23505') {
        throw new Error('此手機已被其他帳號使用，請更換手機號碼')
      }
      throw upErr
    }

    originalPhone.value = authedPhone
    phoneState.value.asked = false
    phoneState.value.code = ''
    phoneState.value.sent = '手機驗證完成，已綁定到會員資料'
  } catch (e) {
    const msg = String(e?.message || '')
    const code = e?.code

    if (
      msg.includes('phone number has already been registered') ||
      code === '23505'
    ) {
      phoneState.value.error = t('profile.phone.alreadyRegistered')
      resetPhoneInput()
    } else {
      phoneState.value.error = t('common.operationFailed')
    }
  }
}

function startPhoneCooldown(seconds) {
  if (phoneCooldownTimer) clearInterval(phoneCooldownTimer)
  phoneState.value.cooldown = seconds

  phoneCooldownTimer = setInterval(() => {
    if (phoneState.value.cooldown <= 1) {
      clearInterval(phoneCooldownTimer)
      phoneCooldownTimer = null
      phoneState.value.cooldown = 0
      return
    }
    phoneState.value.cooldown -= 1
  }, 1000)
}

/** -------- 修改密碼 -------- */
async function onChangePassword() {
  pwdState.value.error = ''
  pwdState.value.success = ''
  pwdState.value.loading = true
  try {
    const p1 = (pwdForm.value.password || '').trim()
    const p2 = (pwdForm.value.confirm || '').trim()
    if (!p1 || p1.length < 8) throw new Error('新密碼至少 8 碼')
    if (p1 !== p2) throw new Error('兩次輸入密碼不一致')

    const { error } = await supabase.auth.updateUser({ password: p1 })
    if (error) throw error

    pwdState.value.success = '密碼已更新'
    pwdForm.value.password = ''
    pwdForm.value.confirm = ''
    setTimeout(() => { openPassword.value = false }, 600)
  } catch (e) {
    pwdState.value.error = e?.message || '密碼更新失敗'
  } finally {
    pwdState.value.loading = false
  }
}

/** -------- 綁定他人邀請碼 -------- */
async function onBindInviteCode() {
  state.value.error = ''
  state.value.success = ''

  const raw = (inviteInput.value || '').trim().toUpperCase()

  if (!raw) {
    state.value.error = '請先輸入邀請碼'
    return
  }

  // 防呆：不能填自己的邀請碼
  if (raw === (inviteCode.value || '').toUpperCase()) {
    state.value.error = '不能使用自己的邀請碼'
    return
  }

  state.value.saving = true
  try {
    // 再確認一次自己是不是已經綁定過（避免有人用奇怪方式繞過前端）
    const { data: selfRow, error: selfErr } = await supabase
      .from('user_profiles')
      .select('invited_by')
      .eq('id', userId.value)
      .maybeSingle()

    if (selfErr) throw selfErr
    if (selfRow?.invited_by) {
      inviteLocked.value = true
      state.value.error = '已經使用過邀請碼，無法重複綁定'
      return
    }

    // 查詢這組邀請碼是否存在
    const { data: inviterRows, error: inviterErr } = await supabase
      .rpc('find_inviter_by_code', { code: raw })

    if (inviterErr) throw inviterErr

    const inviter = inviterRows && inviterRows[0]

    if (!inviter) {
      state.value.error = '查無此邀請碼，請確認是否輸入正確'
      return
    }


    if (inviterErr) throw inviterErr
    if (!inviter) {
      state.value.error = '查無此邀請碼，請確認是否輸入正確'
      return
    }

    // 寫回目前使用者：invited_by + invited_code
    const { error: updateErr } = await supabase
      .from('user_profiles')
      .update({
        invited_by: inviter.id,
        invited_code: raw
      })
      .eq('id', userId.value)

    if (updateErr) throw updateErr

    inviteLocked.value = true
    inviteInput.value = raw
    state.value.success = '邀請碼已成功綁定'
  } catch (e) {
    console.error('[onBindInviteCode] failed:', e)
    state.value.error = e?.message || '綁定邀請碼時發生錯誤'
  } finally {
    state.value.saving = false
  }
}


</script>

<style scoped>
/* ===== 基本框架 ===== */
.profile-page { max-width: 720px; margin: 0 auto; padding: 24px; }
.card {
  background: #fff; border-radius: 14px; box-shadow: 0 10px 30px rgba(0,0,0,0.06);
  padding: 20px 20px 16px;
}
.card-title { margin: 0 0 12px; font-size: 20px; font-weight: 700; letter-spacing: .02em; }

.form-row { display: grid; grid-template-columns: 160px 1fr; gap: 12px; align-items: center; margin: 12px 0; }
.form-label { text-align: right; color: #333; font-weight: 600; }
.input-col { display: grid; gap: 6px; }

/* ===== 表單基礎 ===== */
.form-control {
  width: 100%; padding: 10px 12px;
  border: 1px solid #e5e7eb; border-radius: 10px;
  outline: none; box-sizing: border-box;
}
.form-control:focus { border-color: #111; }
.form-control::placeholder { color: #b6bfc9; opacity: 1; }
input.form-control, textarea.form-control { height: 40px; line-height: 40px; }
select.form-control { height: 40px; line-height: normal; padding: 8px 28px 8px 12px; -webkit-appearance: none; appearance: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* 唯讀 */
.readonly { padding: 10px 12px; background: #fafafa; border: 1px dashed #e5e7eb; border-radius: 10px; color: #555; }

/* ===== 地址：上排固定三格，路名永遠在下一行 ===== */
.select-row { display: grid; grid-template-columns: 1fr 1fr 110px; gap: 10px; align-items: center; }
.select-row > * { min-width: 0; }
.zip-input { width: 110px; }
/* 強制「路名」下一行 */
.form-row:has(> .input-col > .select-row) .input-col { grid-auto-flow: row; }
.form-row:has(> .input-col > .select-row) .input-col > .select-row { grid-column: 1 / -1; grid-row: 1; }
.form-row:has(> .input-col > .select-row) .input-col > input.form-control { grid-column: 1 / -1; grid-row: 2; }
.form-row:has(> .input-col > .select-row) .input-col > .hint { grid-column: 1 / -1; grid-row: 3; }

/* ===== 其他元件 ===== */
.email-input-row { display: grid; grid-template-columns: 1fr auto; gap: 8px; align-items: center; }
.btn { border: 0; border-radius: 10px; padding: 10px 14px; cursor: pointer; }
/* 專門客製「邀請碼輸入那一排」 */
.invite-row {
  align-items: flex-start;
}

/* 讓說明文字在這一排佔滿整行（不跟 input 擠同一格） */
.invite-row .invite-hint {
  grid-column: 1 / -1;
}

/* 手機版：input 第一行；按鈕第二行；說明第三行 */
@media (max-width: 900px) {
  .invite-row {
    grid-template-columns: 1fr; /* 變成一欄直排 */
  }

  .invite-row .btn-mini {
    width: 100%;        /* 按鈕吃滿比較好點 */
    justify-self: flex-start;
  }

  .invite-row .invite-hint {
    grid-column: 1 / -1;  /* 再保險一次，確定是新的一行 */
  }
}

.btn-mini { padding: 6px 10px; border-radius: 8px; background: #111; color: #fff; }
.btn-primary { background: #111; color: #fff; }
.btn-ghost { background: transparent; color: #111; }

.verify-pill { padding: 6px 10px; border-radius: 999px; font-size: 12px; }
.verify-pill.ok { background: #e8faf0; color: #0f7a3b; }
.verify-pill.pending { background: #fff7e6; color: #ad6800; }
.verify-pill.danger { background: #fdecea; color: #a0131a; }

.hint { grid-column: 2 / 3; color: #888; font-size: 12px; }
.actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 12px; }

.alert { padding: 10px 12px; border-radius: 10px; margin: 10px 0; }
.alert-success { background: #e8faf0; color: #0f7a3b; }
.alert-danger { background: #fdecea; color: #a0131a; }

.req { color: #e11d48; margin-left: 4px; }

.spinner {
  width: 14px; height: 14px; display: inline-block; margin-right: 6px;
  border: 2px solid #fff; border-right-color: transparent; border-radius: 50%;
  animation: sp 0.8s linear infinite;
}
@keyframes sp { to { transform: rotate(360deg); } }

/* 修改密碼彈窗（不依賴 Bootstrap） */
.pw-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 11000; /* 確保蓋過 header / 其他浮層 */
}
.pw-modal .modal-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0,0,0,.18);
  padding: 18px;
}


/* ===== 手機（< 900px） ===== */
@media (max-width: 900px) {
  .select-row { grid-template-columns: 1fr; gap: 8px; }
  .zip-input { width: 100%; max-width: none !important; }
  .select-row .form-control { font-size: 14px; }
  .form-row { gap: 8px; }
}

.verify-status-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 小問號按鈕 */
.icon-help-btn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #4b5563;
  font-size: 13px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.icon-help-btn:hover {
  background: #111;
  color: #fff;
}

/* 驗證說明 modal 的小調整 */
.verify-help-card {
  max-width: 520px;
}

/* Modal header：標題 + 關閉按鈕 */
.verify-help-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.icon-close-btn {
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  color: #6b7280;
}
.icon-close-btn:hover {
  color: #111;
}

/* 三個狀態的排列 */
.verify-help-grid {
  display: grid;
  gap: 14px;
}

.verify-help-item {
  padding: 10px 12px;
  border-radius: 12px;
  background: #f9fafb;
}

.verify-help-item .verify-pill {
  display: inline-block;
  margin-bottom: 6px;
}

/* 說明文字 */
.verify-help-caption {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #4b5563;
}

/* 小螢幕也不用特別調整，Modal 自己會縮，這裡保留預設就好 */
@media (max-width: 480px) {
  .verify-help-item {
    padding: 8px 10px;
  }
  .verify-help-caption {
    font-size: 12px;
  }
}
.full-row {
  grid-column: 1 / -1; /* 橫跨整個欄位 */
}

.error-text {
  color: #a0131a;
}

/* ===== Mobile layout overhaul ===== */
@media (max-width: 600px) {
  .profile-page {
    padding: 12px;
  }

  .card {
    padding: 16px;
    border-radius: 14px;
  }

  /* 每一列：改成上下排列 */
  .form-row {
    grid-template-columns: 1fr;
    gap: 6px;
    align-items: start;
    margin: 14px 0;
  }

  .form-label {
    text-align: left;
    font-size: 14px;
    line-height: 1.2;
  }

  /* hints 不要再鎖在第2欄，手機要整行 */
  .hint {
    grid-column: 1 / -1;
    font-size: 12px;
    line-height: 1.4;
  }

  /* input + button：手機改成直排 */
  .email-input-row {
    grid-template-columns: 1fr;
    gap: 8px;
    align-items: stretch;
  }

  .btn-mini {
    width: 100%;
    justify-content: center;
    padding: 10px 12px;
  }

  /* 地址選單本來就有手機規則，但再補強一下間距 */
  .select-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  /* 右側那欄（input-col）不要被 grid 的怪規則影響 */
  .input-col {
    gap: 8px;
  }

  /* actions 兩顆按鈕手機改直排更舒服 */
  .actions {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }

  .actions .btn {
    width: 100%;
  }

  /* 驗證狀態那行避免擠在一起 */
  .verify-status-row {
    justify-content: space-between;
  }
}


</style>
